import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNow } from '../useNow.ts';
import { faultOf, isSupplyFault, nextAttemptInMs, type FaultClass } from './fault.ts';

/* ==========================================================================
   What the console is actually reading, and what the browser actually did.

   Two independent registers, deliberately kept apart, because during an
   outage they answer two different questions and one of them has to still be
   true when the other is red:

     SOURCES   read off the TanStack cache — which endpoints this console has
               mounted, which of them answered, and when each last did. This
               is the console's own account of itself.
     ATTEMPTS  read off `PerformanceResourceTiming` — every /api request this
               browser really made, at its real timestamp, with the status the
               network stack really saw. Not one figure in it comes from the
               API, which is the whole point: a captive portal and a dead API
               produce the same `Failed to fetch` string, and the operator's
               next action is completely different in the two cases.

   Neither invents. Where the browser does not report resource timings the
   attempt strip says so and draws nothing.
   ========================================================================== */

/* --------------------------------------------------------------------------
   SOURCES — off the query cache, once a second, from the queries that are
   already mounted. No second registry: the truth is in the cache, and
   duplicating it is how two parts of a console end up disagreeing about
   whether it is connected.
   -------------------------------------------------------------------------- */

export interface Source {
  /** The serialised query key. Stable across renders, unique per source. */
  id: string;
  /** The key's head — `robots`, `stats`, `commands`. */
  label: string;
  /** The rest of the key, as the machine wrote it. Null when there is none. */
  scope: string | null;
  state: 'answering' | 'failing' | 'unread';
  /** The error verbatim, when it failed. */
  reason: string | null;
  /** Consecutive failures since the last good read. */
  runs: number;
  lastGoodIso: string | null;
  /** ms since the last good read. Null = nothing has ever landed. */
  ageMs: number | null;
  fetching: boolean;
  /**
   * The cadence this console asked for, in ms — read off the observer's own
   * `refetchInterval`, not guessed from the gap between attempts. Null where
   * the source is read on demand.
   */
  everyMs: number | null;
  /** Nothing answered at the transport layer — refused, DNS, CORS, the cable. */
  down: boolean;
  cls: FaultClass | null;
  /** The status the API answered with, where it answered at all. */
  status: number | null;
  /**
   * ms until the console tries this source again on its own, off the same
   * ladder `queryClient.ts` installs. Null = it will not try again by itself.
   */
  nextInMs: number | null;
  /**
   * The failure is about the console's supply, not about one object. A 404 on
   * a deregistered robot is a healthy API answering completely; it must not
   * make the console claim an outage.
   */
  supply: boolean;
}

/* THE MEMORY, AND WHY IT HAS TO EXIST.

   query-core clears `error` and drops `status` back to `pending` the moment a
   new fetch starts on a query that has never held data. A ledger read straight
   off the cache therefore reports "0 of 4 failing" for the second and a half
   every poll spends in flight — and the diagnostics rail, which is gated on
   that count, would appear and disappear on a five-second beat for the whole
   duration of an outage.

   So the last committed error per query is remembered, from the cache's own
   subscription rather than by sampling it, and forgotten the instant that
   query succeeds or is dropped. It is not a second source of truth: every
   entry is something the cache said, kept only for as long as the cache has
   nothing to replace it with. */
const remembered = new Map<
  string,
  { reason: string; cls: FaultClass; status: number | null; runs: number }
>();

/* THE LEDGER MOVES WHEN THE CACHE DOES, NOT WHEN THE CLOCK DOES.

   The ledger used to be recomputed on the 1 Hz clock that ages the rows. That
   is right for the ages and wrong for the states: a panel's own read tag flips
   to `No reply` the instant its observer commits the error, while the ledger —
   and therefore the console verdict and the rail that is gated on it — waited
   for the next tick. For up to a second the header said `4 answering · 0
   failing` over a screen of panels that all said `No reply`, which is the one
   contradiction a trust surface cannot afford.

   So the cache's own subscription drives a serial, and the ledger re-reads on
   that as well as on the clock. Same single source of truth, no polling of it. */
const LEDGER_EVENTS: ReadonlySet<string> = new Set([
  'added',
  'removed',
  'updated',
  'observerAdded',
  'observerRemoved',
]);

/* Epoch ms at which each still-unanswered read began. See `Supply.openedAt`.
   Module scope rather than a ref, because the answer has to be the same for
   every skeleton on the surface and they do not share a component. */
const waiting = new Map<string, number>();

let churn = 0;
let scheduled = false;
const cacheWatchers = new Set<() => void>();

/* Deferred to a microtask, always. Mounting a query emits its cache event
   DURING the render that mounted it, and notifying a store subscriber from
   inside another component's render is a setState-in-render — React says so,
   and it is a real re-entrancy bug, not a lint. One tick later the render has
   committed and the ledger updates on the same frame either way. */
function bumpCache(): void {
  churn += 1;
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    cacheWatchers.forEach((w) => w());
  });
}

function cacheSerial(): number {
  return churn;
}

function useFaultMemory(): number {
  const qc = useQueryClient();
  useEffect(() => {
    const cache = qc.getQueryCache();
    for (const q of cache.getAll()) note(q.queryHash, q.state);
    return cache.subscribe((event) => {
      if (event.type === 'removed') {
        remembered.delete(event.query.queryHash);
      } else {
        note(event.query.queryHash, event.query.state);
      }
      /* Only the events that change what the ledger PRINTS. `observerResults-
         Updated` fires whenever a subscriber re-renders, so waking on it would
         mean this hook's own render woke it again, forever. */
      if (LEDGER_EVENTS.has(event.type)) bumpCache();
    });
  }, [qc]);

  return useSyncExternalStore(subscribeCache, cacheSerial, cacheSerial);
}

function subscribeCache(fn: () => void): () => void {
  cacheWatchers.add(fn);
  return () => {
    cacheWatchers.delete(fn);
  };
}

function note(
  hash: string,
  state: { status: string; error: unknown; fetchFailureCount: number },
): void {
  if (state.status === 'success') {
    remembered.delete(hash);
    return;
  }
  if (state.status !== 'error') return;
  const f = faultOf(state.error);
  if (f === null) return;
  remembered.set(hash, {
    reason: f.reason,
    cls: f.class,
    status: f.status,
    runs: Math.max(1, state.fetchFailureCount),
  });
}

/* --------------------------------------------------------------------------
   Is this console being supplied AT ALL?

   The cheap half of `useOutage` — no clock, no ages, no sort — because it is
   asked by every skeleton on the surface rather than once by the banner.

   It exists to answer one question the skeletons cannot answer for themselves:
   `useStanding` is told what to draw by whichever panel mounted it, and a
   panel that computes its own state off TanStack's `isError` is wrong for the
   whole time a poll is in flight (query-core clears `error` and drops `status`
   back to `pending` on every refetch of a query that has never held data). A
   panel wired that way asks for grey blocks for ever, and a grey block is a
   promise that a value is on its way.

   So the module checks the promise against the cache before it draws one. If
   something is failing at the transport layer and NOT ONE mounted source is
   answering, no request in flight on this surface is going to land, and no
   block anywhere on it is honest — whatever the panel believes.
   -------------------------------------------------------------------------- */

export interface Supply {
  /** Mounted reads whose last committed attempt failed at the transport layer. */
  down: number;
  /** Mounted reads with a good result on record. */
  answering: number;
  /**
   * Nothing is getting through. Not "one endpoint is broken" — that leaves the
   * others answering — but the whole supply: the link, the gateway, the API.
   */
  starved: boolean;
  /**
   * A first read is genuinely in flight somewhere on this surface and has not
   * failed yet. The surface is OPENING, not stopped.
   */
  opening: boolean;
  /** Mounted reads carrying a committed fault of ANY class. */
  faults: number;
  /**
   * HOW LONG THIS SURFACE HAS BEEN WAITING — epoch ms at which the OLDEST
   * still-unanswered first read began. Null when nothing is opening.
   *
   * The word `Reading…` is not a state, it is a claim that work is in
   * progress, and a claim with no duration on it is unfalsifiable: a hung API
   * and a fast one produce the same screen for ever. Measured on a stalled
   * load, 300 ms and 3 s of `Reading…` differed by 163 pixels, all of them the
   * header clock. The failure states have carried their age since the first
   * round (`Nothing has reached this console for 26s · next attempt 1s`); this
   * is the same sentence for the state before them.
   *
   * The oldest rather than each panel's own, because the shapes are drawn from
   * a context that carries no query identity. In the two cases that matter it
   * is exact: on a cold start every source begins within a frame of the
   * others, and a single slow endpoint under a healthy console is the only
   * thing still opening. Callers that DO hold the read — `ReadBody` — pass
   * their own figure instead (see `ReadingNote`).
   */
  openedAt: number | null;
}

export function useSupply(): Supply {
  const qc = useQueryClient();
  useFaultMemory();
  /* Read during render rather than off a 1 Hz tick: this hook is called by
     every skeleton on the surface and none of them should repaint on a clock.
     It does not need one — the cache emits an event on every attempt, success
     or failure, which is exactly when the answer can change. */
  const now = Date.now();

  const mounted = qc.getQueryCache().getAll().filter((q) => q.getObserversCount() > 0);

  const classOf = (q: (typeof mounted)[number]): FaultClass | null => {
    const s = q.state;
    if (s.status === 'success') return null;
    const live = s.status === 'error' ? faultOf(s.error) : null;
    const kept = s.data === undefined ? remembered.get(q.queryHash) : undefined;
    return live?.class ?? kept?.cls ?? null;
  };

  /* Transport only. A 503 is the API answering — badly, but answering — and an
     operator watching a rolling deploy should still see the console working on
     it. `starved` is for the case where the console cannot reach anything. */
  const dead = (q: (typeof mounted)[number]): boolean => {
    const cls = classOf(q);
    return cls === 'link' || cls === 'timeout';
  };

  /* A FIRST READ THAT IS STILL IN FLIGHT AND HAS NOT FAILED YET IS NOT
     EVIDENCE OF ANYTHING, AND IT MUST NOT BE OVERRULED.

     One endpoint refused at t+3.5s while the rest of the surface is still on
     its first request would otherwise starve the whole page for the half
     second before they land — every skeleton torn down and rebuilt, which is
     the flicker this module exists to remove. During a real outage every
     source carries a committed fault (the `remembered` map keeps it across the
     refetch window), so this never blocks the verdict when it matters. The
     `fetching` test is load-bearing: a disabled query sits `pending` for ever
     and would veto `starved` for the whole session. */
  /* WHEN EACH UNANSWERED READ STARTED.

     TanStack records `dataUpdatedAt` and `errorUpdatedAt` and nothing for the
     attempt currently in flight, so the one timestamp the loading state needs
     is the one the cache does not keep. It is recorded here, keyed by query
     hash, the first time a query is seen pending-and-fetching, and dropped the
     moment it is not — so the figure is the age of THIS wait and never of a
     previous one. Idempotent (it only ever writes a key it has not seen), so
     several skeletons calling this in one render pass all read the same value. */
  let opening = false;
  let openedAt: number | null = null;
  for (const q of mounted) {
    const s = q.state;
    if (s.status === 'pending' && s.fetchStatus === 'fetching' && classOf(q) === null) {
      opening = true;
      const at = waiting.get(q.queryHash) ?? now;
      waiting.set(q.queryHash, at);
      openedAt = openedAt === null ? at : Math.min(openedAt, at);
    } else {
      waiting.delete(q.queryHash);
    }
  }

  /* When the trouble started, at the latest: the most recent good read among
     the sources that are now failing. Same rule as `useOutage.supplying` — a
     success is evidence the link is up only if it happened AFTER that, so the
     one-shot reads a surface makes at mount stop vouching for a link that has
     since gone. */
  let began: number | null = null;
  let down = 0;
  for (const q of mounted) {
    if (!dead(q)) continue;
    down += 1;
    const at = q.state.dataUpdatedAt;
    if (at > 0) began = began === null ? now - at : Math.min(began, now - at);
  }

  let answering = 0;
  for (const q of mounted) {
    const s = q.state;
    if (s.status !== 'success' || s.dataUpdatedAt === 0) continue;
    const age = now - s.dataUpdatedAt;
    if (began === null ? age <= 30_000 : age < began) answering += 1;
  }

  let faults = 0;
  for (const q of mounted) if (classOf(q) !== null) faults += 1;

  return {
    down,
    answering,
    faults,
    opening,
    openedAt,
    starved: down > 0 && answering === 0 && !opening,
  };
}

/* --------------------------------------------------------------------------
   THE LINK CAME BACK, AND NOBODY TOLD THE ONE-SHOT READS.

   `queryClient.ts` re-reads everything on window focus and on the browser's
   `reconnect` event. Neither of those fires for the failure this console
   actually has: a site link, a gateway or the API goes away and comes back
   while the tab is in the foreground the whole time and `navigator.onLine`
   never moves. A POLLED source recovers by itself on its next tick. A source
   read ONCE per surface — `/api/auth/me`, `/api/morphologies`,
   `/api/command-profiles`, a robot's enrolment — has no next tick, so it stays
   in its error for the rest of the session.

   Measured: an eight-minute outage on /fleet, healed. Every polled panel came
   back within five seconds; the banner went on reading `PARTIAL OUTAGE · 2 of
   8 sources have stopped answering · auth · command-profiles` over a fully
   working console, and would have done so until the operator reloaded. An
   outage indicator that cannot clear itself is worse than none: the next real
   one is not believed.

   So the rule, and it is deliberately narrow:

     A read WITH NO SCHEDULE OF ITS OWN that gave up at the TRANSPORT layer is
     tried again as soon as some other read has demonstrably got through, and
     never otherwise.

   · One-shot reads only — a source with a `refetchInterval` has a next tick
     and will heal on it. Sweeping polled sources too would put a permanently
     refused endpoint (a proxy rule, a blocked path) on a 2 s loop for the rest
     of the session while the rest of the console works perfectly, which is a
     worse bug than the one this fixes: the sweep would re-arm every time any
     other source answered.
   · Transport only (`link` / `timeout`). A 401, a 403, a 404 and a 500 are the
     API answering; nothing about another endpoint succeeding makes them wrong,
     and re-asking is how a console generates four hundred rejected-token log
     lines for whoever is on call.
   · The proof has to be NEWER than the failure. A success that landed before
     the source gave up proves nothing.
   · At most one sweep every 2s, and never over a source that is already in
     flight — the retry ladder in `fault.ts` owns the first few seconds after a
     failure, and this must not race it.

   The result is that the recovery is as automatic as the failure was, and the
   banner's disappearance is the console's own doing rather than something the
   operator has to press Retry to find out about.
   -------------------------------------------------------------------------- */

const RELINK_EVERY_MS = 2_000;
/* And a ceiling, because "the link is back" is evidence about the LINK and not
   about this endpoint. A one-shot path that is refused on its own — a proxy
   rule, a route that was removed, a gateway that only forwards some prefixes —
   would otherwise be re-asked every time any other source answered, for the
   rest of the session. Three attempts is the same budget `fault.ts` gives a
   transport failure; after that the source waits for the operator's Retry,
   which is on screen in the banner. Cleared the moment it succeeds. */
const RELINK_MAX = 3;
const sweeps = new Map<string, number>();

export function useRelink(): void {
  const qc = useQueryClient();
  /* Not a clock: this re-runs when the cache moves, which is exactly when the
     answer can change. `useSources` is already subscribed for the rail. */
  const sources = useSources();
  const swept = useRef(0);

  useEffect(() => {
    const now = Date.now();
    if (now - swept.current < RELINK_EVERY_MS) return;

    const mounted = qc.getQueryCache().getAll().filter((q) => q.getObserversCount() > 0);

    /* The most recent moment ANY read got through. That is the evidence. */
    let proof = 0;
    for (const q of mounted) {
      if (q.state.dataUpdatedAt > proof) proof = q.state.dataUpdatedAt;
    }
    if (proof === 0) return;

    const stranded = mounted.filter((q) => {
      const st = q.state;
      if (st.status === 'success') {
        sweeps.delete(q.queryHash);
        return false;
      }
      if (st.fetchStatus !== 'idle') return false;
      if ((sweeps.get(q.queryHash) ?? 0) >= RELINK_MAX) return false;
      const cls = st.status === 'error' ? faultOf(st.error)?.class ?? null : null;
      const kept = st.data === undefined ? remembered.get(q.queryHash)?.cls ?? null : null;
      const c = cls ?? kept;
      if (c !== 'link' && c !== 'timeout') return false;
      /* A polled source has a next tick and heals on it. */
      if (cadenceOf(q) !== null) return false;
      /* Only where the console has proof of a working link SINCE this one gave
         up. `errorUpdatedAt` is 0 on a query that has never committed a
         failure, which cannot happen here, but the comparison is written to be
         safe if it ever does. */
      return st.errorUpdatedAt > 0 && proof > st.errorUpdatedAt;
    });
    if (stranded.length === 0) return;

    swept.current = now;
    const hashes = new Set(stranded.map((q) => q.queryHash));
    for (const h of hashes) sweeps.set(h, (sweeps.get(h) ?? 0) + 1);
    void qc.refetchQueries({ predicate: (q) => hashes.has(q.queryHash) });
  }, [qc, sources]);
}

export function useSources(): readonly Source[] {
  const qc = useQueryClient();
  const now = useNow(true);
  /* Subscribing is the point; the serial is only what wakes this hook. There is
     deliberately no memo keyed on it — a single pass over the handful of
     queries this console mounts costs nothing, and a memo keyed on a token its
     body does not read is a lie the linter is right to object to (same reason
     `useAttempts` below has none). */
  useFaultMemory();

  {
    const rows: Source[] = [];
    for (const q of qc.getQueryCache().getAll()) {
      if (q.getObserversCount() === 0) continue;
      const key = q.queryKey as readonly unknown[];
      const head = typeof key[0] === 'string' ? key[0] : JSON.stringify(key[0]);
      const rest = key.slice(1).filter((k) => k !== undefined && !isEmptyObject(k));
      const s = q.state;
      const live = s.status === 'error' ? faultOf(s.error) : null;
      const kept = s.data === undefined ? remembered.get(q.queryHash) : undefined;
      const cls = live?.class ?? kept?.cls ?? null;
      const reason = live?.reason ?? kept?.reason ?? null;
      const status = live?.status ?? kept?.status ?? null;
      const failing = cls !== null;
      const runs = live !== null ? Math.max(1, s.fetchFailureCount) : (kept?.runs ?? 0);
      const everyMs = cadenceOf(q);
      rows.push({
        id: q.queryHash,
        label: head ?? 'source',
        scope: rest.length === 0 ? null : rest.map(scopeOf).join(' · '),
        state: failing ? 'failing' : s.status === 'success' ? 'answering' : 'unread',
        reason,
        runs,
        lastGoodIso: s.dataUpdatedAt > 0 ? new Date(s.dataUpdatedAt).toISOString() : null,
        ageMs: s.dataUpdatedAt > 0 ? Math.max(0, now - s.dataUpdatedAt) : null,
        fetching: s.fetchStatus === 'fetching',
        everyMs,
        down: cls === 'link',
        cls,
        status,
        /* Mid-retry there is no settled failure stamp to count from, so the
           strip says `reading…` rather than counting down to an attempt that
           is already in flight. */
        nextInMs:
          cls === null || s.fetchStatus === 'fetching' || s.errorUpdatedAt === 0
            ? null
            : nextAttemptInMs(
                { class: cls, status, reason: reason ?? '', recovery: 'auto', retried: runs > 0 },
                s.errorUpdatedAt,
                runs,
                everyMs,
                now,
              ),
        supply: cls !== null && isSupplyFault(cls),
      });
    }
    /* Broken first, then alphabetical — a ledger read during an incident is
       read from the top, and the row the operator needs is the one that
       stopped. Within a state the order is stable across polls so the list
       does not reshuffle under a moving cursor. */
    rows.sort((a, b) => {
      const rank = (r: Source) => (r.state === 'failing' ? 0 : r.state === 'unread' ? 1 : 2);
      return rank(a) - rank(b) || a.id.localeCompare(b.id);
    });
    return rows;
  }
}

/* The declared poll interval, off the observer that declared it. A console
   that printed a cadence inferred from the spacing of the last few attempts
   would print `4s` for a 5s poll that lost a packet, and the whole point of
   this ledger is that every figure in it is something the console knows
   rather than something it estimated. */
function cadenceOf(q: { observers: readonly { options: { refetchInterval?: unknown } }[] }): number | null {
  for (const o of q.observers) {
    const every = o.options.refetchInterval;
    if (typeof every === 'number' && every > 0) return every;
  }
  return null;
}

function isEmptyObject(v: unknown): boolean {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0;
}

function scopeOf(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v) ?? '';
}

/* --------------------------------------------------------------------------
   ATTEMPTS — every /api request this document made, at its own timestamp.

   A ledger says WHICH endpoint stopped and how many reads in a row it has
   lost. It cannot say whether the endpoint is dead or flapping, and those
   have different next actions: a solid run of red is an outage to escalate;
   a red-and-steel alternation is a link losing a third of its packets and the
   console is still partly usable.

   `formant-timeline-data-presence-scrubber.png`'s presence band, turned on
   the console itself. Nothing is interpolated and nothing is smoothed: where
   the console made no request, the lane is empty, and that gap is the
   reading.
   -------------------------------------------------------------------------- */

export interface Attempt {
  /** Epoch ms, the request's own start. */
  t: number;
  ok: boolean;
  /** Round trip, ms. Null where nothing came back. */
  ms: number | null;
  bytes: number;
}

const WINDOW_MS = 120_000;
const RING_MAX = 900;

interface Entry extends Attempt {
  path: string;
}

const ring: Entry[] = [];
const watchers = new Set<() => void>();
let serial = 0;
let observer: PerformanceObserver | null = null;
let supported = true;

function bump(): void {
  serial += 1;
  watchers.forEach((w) => w());
}

function record(list: readonly PerformanceEntry[]): void {
  const origin = performance.timeOrigin;
  let added = false;
  for (const e of list) {
    if (e.entryType !== 'resource') continue;
    const r = e as PerformanceResourceTiming;
    const path = pathOf(r.name);
    if (path === null) continue;
    /* `responseStatus` is the browser's own verdict and is the only field
       that distinguishes a 503 from a 200. Where it is not implemented, a
       response that carried no bytes and no protocol is the honest fallback
       for "nothing came back". */
    const status = (r as { responseStatus?: number }).responseStatus;
    const ok =
      typeof status === 'number' && status > 0
        ? status >= 200 && status < 400
        : r.responseEnd > 0 && (r.transferSize > 0 || r.encodedBodySize > 0 || r.nextHopProtocol !== '');
    ring.push({
      t: origin + r.startTime,
      ok,
      ms: ok ? Math.round(r.duration) : null,
      bytes: r.transferSize,
      path,
    });
    added = true;
  }
  if (!added) return;
  if (ring.length > RING_MAX) ring.splice(0, ring.length - RING_MAX);
  bump();
}

/** `/api/robots?active_only=true` → `/api/robots`. Query strings are the same source. */
function pathOf(url: string): string | null {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.startsWith('/api/') ? u.pathname : null;
  } catch {
    return null;
  }
}

function subscribe(fn: () => void): () => void {
  watchers.add(fn);
  if (observer === null && typeof PerformanceObserver !== 'undefined') {
    try {
      observer = new PerformanceObserver((l) => record(l.getEntries()));
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      supported = false;
      observer = null;
    }
  }
  return () => {
    watchers.delete(fn);
  };
}

function snapshot(): number {
  return serial;
}

export interface AttemptWindow {
  from: number;
  to: number;
  byPath: ReadonlyMap<string, readonly Attempt[]>;
  ok: number;
  failed: number;
  bytes: number;
  /** Median round trip of the reads that landed, ms. Null when none did. */
  p50: number | null;
  /** Epoch ms of the newest read that actually landed. Null = none in window. */
  okAt: number | null;
  supported: boolean;
}

export function useAttempts(): AttemptWindow {
  const now = useNow(true);
  /* The ring is a mutable module singleton, so the subscription's serial is
     what makes this component re-render when a request lands. Nothing is
     memoised on it: the window is a single pass over at most 900 entries,
     once a second, and a memo keyed on a token it does not read is a lie the
     linter is right to object to. */
  useSyncExternalStore(subscribe, snapshot, snapshot);

  /* Clamped to the session's own start: a console open for twenty seconds has
     not "made no requests for the first hundred", it did not exist, and
     painting 83% of the lane empty would be the strip inventing an outage out
     of the page load. */
  const from = Math.max(now - WINDOW_MS, performance.timeOrigin);
  const byPath = new Map<string, Attempt[]>();
  let ok = 0;
  let failed = 0;
  let carried = 0;
  let newestOk = 0;
  const rtts: number[] = [];
  for (const e of ring) {
    if (e.t < from) continue;
    let bucket = byPath.get(e.path);
    if (bucket === undefined) {
      bucket = [];
      byPath.set(e.path, bucket);
    }
    bucket.push({ t: e.t, ok: e.ok, ms: e.ms, bytes: e.bytes });
    if (e.ok) ok += 1;
    else failed += 1;
    carried += e.bytes;
    /* SUCCESSFUL READS ONLY, AND THIS IS NOT A NIT.

       THE BAR §11.10: "latency renders `—`, never a number ... an invented
       latency figure is the single worst defect available to this product",
       because it is the one number an operator trusts to decide whether to
       move a machine. A refused connection still produces a
       PerformanceResourceTiming, and its duration is the time the stack took
       to give up — 8–14ms on a closed local port. Folding those in had the
       supply strip print `Round trip 12 ms` across a screen on which nothing
       had arrived for a minute and a half: a healthy-looking latency measured
       entirely from failures. A round trip that did not complete is not a
       round trip, so it is not in the sample, and with no completed reads in
       the window the figure is `—`. */
    if (e.ok && e.ms !== null) rtts.push(e.ms);
    if (e.ok && e.t > newestOk) newestOk = e.t;
  }
  rtts.sort((a, b) => a - b);

  return {
    from,
    to: now,
    byPath,
    ok,
    failed,
    bytes: carried,
    p50: rtts.length === 0 ? null : (rtts[Math.floor(rtts.length / 2)] ?? null),
    okAt: newestOk === 0 ? null : newestOk,
    supported,
  };
}

/* --------------------------------------------------------------------------
   ENDPOINTS, NOT QUERY INSTANCES.

   `useSources` returns one row per mounted query, and the console mounts one
   catalog read per morphology and one telemetry read per selected robot — so a
   fleet register with three dead endpoints reported "3 of 40 sources", a
   fraction that understates a total outage by an order of magnitude and moves
   as the operator selects rows. Every fraction an operator is asked to read —
   the banner's sentence, the supply line, the ledger's eyebrow — counts the
   ENDPOINTS instead: `robots` is one source whether it is read once or thirty
   times, and the three places that print it print the same number.
   -------------------------------------------------------------------------- */
export function endpointCounts(sources: readonly Source[]): {
  total: number;
  failing: number;
  names: string[];
} {
  const all = new Set<string>();
  const bad = new Set<string>();
  for (const s of sources) {
    all.add(s.label);
    if (s.supply) bad.add(s.label);
  }
  return { total: all.size, failing: bad.size, names: [...bad] };
}
